import { apiService } from './api';
import { PaginationParams } from '../types';

// ─── Chart of Accounts ──────────────────────────────────────────────────────

export interface GLAccountDTO {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  accountType: 'Asset' | 'Liability' | 'Equity' | 'Revenue' | 'Expense';
  normalBalance: 'Debit' | 'Credit';
  parentAccountId?: number | null;
  parentAccountName?: string | null;
  isSystemAccount: boolean;
  systemAccountKey?: string | null;
  isActive: boolean;
  currentBalance: number;
}

export interface CreateGLAccountRequest {
  code: string;
  name: string;
  description?: string;
  accountType: string;
  parentAccountId?: number | null;
  isActive: boolean;
}

export type UpdateGLAccountRequest = CreateGLAccountRequest;

// ─── General Ledger ─────────────────────────────────────────────────────────

export interface GLJournalLineDTO {
  id: number;
  accountId: number;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  description?: string | null;
}

export interface GLJournalEntryDTO {
  id: number;
  entryNumber: string;
  entryDate: string;
  description?: string | null;
  sourceModule: 'Manual' | 'Sales' | 'Purchase';
  sourceDocumentType?: string | null;
  sourceDocumentId?: number | null;
  status: 'Draft' | 'Posted' | 'Voided';
  totalDebit: number;
  totalCredit: number;
  postedAt?: string | null;
  createdAt: string;
  lines: GLJournalLineDTO[];
}

export interface GLJournalEntryListDTO {
  id: number;
  entryNumber: string;
  entryDate: string;
  description?: string | null;
  sourceModule: string;
  status: string;
  totalDebit: number;
  totalCredit: number;
}

export interface CreateGLJournalLineRequest {
  accountId: number;
  debit: number;
  credit: number;
  description?: string;
}

export interface CreateGLJournalEntryRequest {
  entryDate: string;
  description?: string;
  lines: CreateGLJournalLineRequest[];
}

export interface GLAccountLedgerLineDTO {
  transactionDate: string;
  entryNumber: string;
  description?: string | null;
  debit: number;
  credit: number;
  runningBalance: number;
}

export interface TrialBalanceRowDTO {
  accountCode: string;
  accountName: string;
  accountType: string;
  debit: number;
  credit: number;
}

// ─── Accounts Receivable ────────────────────────────────────────────────────

export interface ARCustomerBalanceDTO {
  customerId: number;
  customerName: string;
  outstandingBalance: number;
  current: number;
  days1To30: number;
  days31To60: number;
  days61To90: number;
  daysOver90: number;
}

export interface ARLedgerEntryDTO {
  id: number;
  transactionDate: string;
  transactionType: string;
  documentType?: string | null;
  documentId?: number | null;
  documentNumber?: string | null;
  description?: string | null;
  debit: number;
  credit: number;
  runningBalance: number;
}

// ─── Accounts Payable ────────────────────────────────────────────────────────

export interface APSupplierBalanceDTO {
  supplierId: number;
  supplierName: string;
  outstandingBalance: number;
  current: number;
  days1To30: number;
  days31To60: number;
  days61To90: number;
  daysOver90: number;
}

export interface APLedgerEntryDTO {
  id: number;
  transactionDate: string;
  transactionType: string;
  documentType?: string | null;
  documentId?: number | null;
  documentNumber?: string | null;
  description?: string | null;
  debit: number;
  credit: number;
  runningBalance: number;
}

// ─── Service ────────────────────────────────────────────────────────────────

const BASE = '/accounting';

export const chartOfAccountsService = {
  getAll: (includeInactive = false) =>
    apiService.get<GLAccountDTO[]>(`${BASE}/chart-of-accounts`, { includeInactive } as Record<string, unknown>),
  getById: (id: number) => apiService.get<GLAccountDTO>(`${BASE}/chart-of-accounts/${id}`),
  create: (data: CreateGLAccountRequest) => apiService.post<GLAccountDTO>(`${BASE}/chart-of-accounts`, data),
  update: (id: number, data: UpdateGLAccountRequest) => apiService.put<GLAccountDTO>(`${BASE}/chart-of-accounts/${id}`, data),
  delete: (id: number) => apiService.delete<void>(`${BASE}/chart-of-accounts/${id}`),
};

export const generalLedgerService = {
  getJournalEntries: (params?: PaginationParams & { status?: string }) =>
    apiService.get<GLJournalEntryListDTO[]>(`${BASE}/general-ledger/journal-entries`, params as Record<string, unknown>),
  getJournalEntryById: (id: number) =>
    apiService.get<GLJournalEntryDTO>(`${BASE}/general-ledger/journal-entries/${id}`),
  createManualEntry: (data: CreateGLJournalEntryRequest) =>
    apiService.post<GLJournalEntryDTO>(`${BASE}/general-ledger/journal-entries`, data),
  voidJournalEntry: (id: number, reason: string) =>
    apiService.post<GLJournalEntryDTO>(`${BASE}/general-ledger/journal-entries/${id}/void`, { reason }),
  getAccountLedger: (accountId: number, fromDate?: string, toDate?: string) =>
    apiService.get<GLAccountLedgerLineDTO[]>(`${BASE}/general-ledger/accounts/${accountId}/ledger`, { fromDate, toDate } as Record<string, unknown>),
  getTrialBalance: (asOfDate?: string) =>
    apiService.get<TrialBalanceRowDTO[]>(`${BASE}/general-ledger/trial-balance`, asOfDate ? ({ asOfDate } as Record<string, unknown>) : undefined),
};

export const accountsReceivableService = {
  getCustomerBalances: () => apiService.get<ARCustomerBalanceDTO[]>(`${BASE}/accounts-receivable/balances`),
  getCustomerLedger: (customerId: number) =>
    apiService.get<ARLedgerEntryDTO[]>(`${BASE}/accounts-receivable/customers/${customerId}/ledger`),
};

export const accountsPayableService = {
  getSupplierBalances: () => apiService.get<APSupplierBalanceDTO[]>(`${BASE}/accounts-payable/balances`),
  getSupplierLedger: (supplierId: number) =>
    apiService.get<APLedgerEntryDTO[]>(`${BASE}/accounts-payable/suppliers/${supplierId}/ledger`),
};
